import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * THEMES — 1 default (sage) + 12 palettes from user spec.
 * Each theme defines a full token map. The CSS layer in index.css
 * binds every used Tailwind utility class to a `var(--...)` so a
 * new theme themes the entire app without per-theme CSS.
 */
/**
 * Globally enforced typography — every theme uses Sage Clinic's Manrope (display) +
 * Work Sans (body) so the application feels typographically uniform regardless of palette.
 * Each theme's `font` field is kept in the registry for posterity but ignored at runtime.
 */
const UNIFIED_FONT_SANS = '"Work Sans", system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
const UNIFIED_FONT_DISPLAY = '"Manrope", system-ui, sans-serif';

/**
 * Warmer, deeper card surface across every theme. Light themes get a single
 * warm cream tone; dark themes get a deeper warm-tinted surface. This keeps
 * the dual-tone "cool page + warm cards" feel that Sage Clinic uses and the
 * user signed off on.
 */
const WARM_LIGHT_SURFACE   = "#F0E9DC"; // Warm Old-Paper cream — used by all light themes
const WARM_LIGHT_SURFACE_2 = "#E7DFCF"; // Slightly deeper for surface-2
const WARM_LIGHT_BORDER    = "#D9CFB8"; // Border that matches the cream

const WARM_DARK_SURFACE    = "#1F1B16"; // Warm near-black for dark themes
const WARM_DARK_SURFACE_2  = "#2A241B";
const WARM_DARK_BORDER     = "#3A3226";

const isDarkBg = (hex) => {
  // crude luminance — accept #RRGGBB
  if (!hex || hex[0] !== "#" || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.5;
};

/**
 * Per-theme surface overrides — explicitly chosen by the user.
 * Currently only Slate Pro is kept (user reverted the other custom surfaces
 * after preview). Themes not listed here keep the warm cream/dark defaults.
 *   surface   — main card background (was warm cream / warm near-black)
 *   surface2  — slightly darker variant for surface-2
 *   border    — divider/border tone that reads on `surface`
 *   text      — primary text colour ON the surface
 *   textMuted — muted text colour ON the surface
 */
const CUSTOM_SURFACES = {
  slate:    { surface: "#77869A", surface2: "#647386", border: "#93A0B0", text: "#FAFAFA", textMuted: "#D8DDE5" },
};

const make = ({ id, name, emoji, tagline, font, colors }) => {
  const dark = isDarkBg(colors.background);
  const fallbackSurface  = dark ? WARM_DARK_SURFACE   : WARM_LIGHT_SURFACE;
  const fallbackSurface2 = dark ? WARM_DARK_SURFACE_2 : WARM_LIGHT_SURFACE_2;
  const fallbackBorder   = dark ? WARM_DARK_BORDER    : WARM_LIGHT_BORDER;
  const override = CUSTOM_SURFACES[id];
  const surface    = override?.surface   ?? fallbackSurface;
  const surface2   = override?.surface2  ?? fallbackSurface2;
  const border     = override?.border    ?? fallbackBorder;
  const textOnSurface      = override?.text      ?? colors.text;
  const textMutedOnSurface = override?.textMuted ?? colors.textMuted;
  return ({
    id, name, emoji, tagline,
    fontSans: UNIFIED_FONT_SANS,
    fontDisplay: UNIFIED_FONT_DISPLAY,
    fontMono: '"JetBrains Mono", ui-monospace, monospace',
    swatches: [
      { label: "bg", value: colors.background },
      { label: "surface", value: surface },
      { label: "primary", value: colors.primary },
      { label: "accent", value: colors.accent },
      { label: "text", value: textOnSurface },
    ],
    tokens: {
      bg: colors.background,
      surface,
      "surface-2": surface2,
      border,
      text: textOnSurface,
      "text-muted": textMutedOnSurface,
      primary: colors.primary,
      "primary-fg": colors.btnText,
      secondary: colors.secondary,
      accent: colors.accent,
      "nav-bg": surface, // sidebar/header surfaces use the warm card tone too
      "nav-text": textOnSurface,
      "btn-text": colors.btnText,
      shadow: colors.shadow,
      "hero-bg": colors.navBg,
      "hero-text": colors.navText,
    },
  });
};

export const THEMES = {
  sage: {
    id: "sage", name: "Sage Clinic", emoji: "🌿",
    tagline: "Default — calm, clinical, warm",
    fontSans: '"Work Sans", system-ui, sans-serif',
    fontDisplay: '"Manrope", system-ui, sans-serif',
    fontMono: '"JetBrains Mono", ui-monospace, monospace',
    swatches: [
      { label: "bg", value: "#93AFA4" },
      { label: "surface", value: "#F0E9DC" },
      { label: "primary", value: "#4B7665" },
      { label: "accent", value: "#D98C70" },
      { label: "text", value: "#1C1917" },
    ],
    tokens: {
      bg: "#93AFA4", surface: "#F0E9DC", "surface-2": "#E7DFCF",
      border: "#D9CFB8", text: "#1C1917", "text-muted": "#78716C",
      primary: "#4B7665", "primary-fg": "#FFFFFF", secondary: "#6A9783",
      accent: "#D98C70", "nav-bg": "#F0E9DC", "nav-text": "#1C1917",
      "btn-text": "#FFFFFF", shadow: "rgba(75,118,101,0.16)",
      "hero-bg": "#325044", "hero-text": "#FFFFFF",
    },
  },
  ocean: make({ id: "ocean", name: "Ocean Breeze", emoji: "🌊", tagline: "Coastal blues · Georgia serif",
    font: 'Georgia, "Times New Roman", serif',
    colors: { primary: "#0077B6", secondary: "#00B4D8", accent: "#90E0EF", background: "#F0F9FF", surface: "#FFFFFF", surfaceAlt: "#E0F4FF", text: "#03045E", textMuted: "#4A7FA5", border: "#ADE8F4", navBg: "#023E8A", navText: "#CAF0F8", btnText: "#FFFFFF", shadow: "rgba(0,119,182,0.15)" } }),
  forest: make({ id: "forest", name: "Forest Dusk", emoji: "🌿", tagline: "Deep greens · Palatino",
    font: '"Palatino Linotype", Palatino, serif',
    colors: { primary: "#2D6A4F", secondary: "#52B788", accent: "#B7E4C7", background: "#F1FAEE", surface: "#FFFFFF", surfaceAlt: "#D8F3DC", text: "#1B4332", textMuted: "#40916C", border: "#95D5B2", navBg: "#1B4332", navText: "#D8F3DC", btnText: "#FFFFFF", shadow: "rgba(45,106,79,0.15)" } }),
  ember: make({ id: "ember", name: "Ember Glow", emoji: "🔥", tagline: "Warm crimson · Trebuchet",
    font: '"Trebuchet MS", sans-serif',
    colors: { primary: "#C1121F", secondary: "#E85D04", accent: "#F48C06", background: "#FFF8F0", surface: "#FFFFFF", surfaceAlt: "#FFE8CC", text: "#370617", textMuted: "#9D4210", border: "#FFBA08", navBg: "#370617", navText: "#FFD166", btnText: "#FFFFFF", shadow: "rgba(193,18,31,0.15)" } }),
  midnight: make({ id: "midnight", name: "Midnight Studio", emoji: "🌙", tagline: "Cosmic violet · Courier",
    font: '"Courier New", monospace',
    colors: { primary: "#7B2FBE", secondary: "#9D4EDD", accent: "#C77DFF", background: "#10002B", surface: "#1A0040", surfaceAlt: "#240046", text: "#E0AAFF", textMuted: "#9D4EDD", border: "#3C096C", navBg: "#0D001A", navText: "#C77DFF", btnText: "#FFFFFF", shadow: "rgba(123,47,190,0.3)" } }),
  slate: make({ id: "slate", name: "Slate Pro", emoji: "🪨", tagline: "Cool neutrals · Gill Sans",
    font: '"Gill Sans", "Gill Sans MT", sans-serif',
    colors: { primary: "#334155", secondary: "#64748B", accent: "#38BDF8", background: "#F8FAFC", surface: "#FFFFFF", surfaceAlt: "#F1F5F9", text: "#0F172A", textMuted: "#64748B", border: "#CBD5E1", navBg: "#0F172A", navText: "#CBD5E1", btnText: "#FFFFFF", shadow: "rgba(51,65,85,0.12)" } }),
  rose: make({ id: "rose", name: "Rose Gold", emoji: "🌹", tagline: "Romantic crimson · Book Antiqua",
    font: '"Book Antiqua", Palatino, serif',
    colors: { primary: "#BE123C", secondary: "#E11D48", accent: "#FDA4AF", background: "#FFF1F2", surface: "#FFFFFF", surfaceAlt: "#FFE4E6", text: "#4C0519", textMuted: "#9F1239", border: "#FECDD3", navBg: "#4C0519", navText: "#FECDD3", btnText: "#FFFFFF", shadow: "rgba(190,18,60,0.15)" } }),
  arctic: make({ id: "arctic", name: "Arctic Frost", emoji: "❄️", tagline: "Icy cyan · Segoe UI",
    font: '"Segoe UI", Tahoma, sans-serif',
    colors: { primary: "#0E7490", secondary: "#06B6D4", accent: "#67E8F9", background: "#F0FFFE", surface: "#FFFFFF", surfaceAlt: "#CFFAFE", text: "#083344", textMuted: "#0891B2", border: "#A5F3FC", navBg: "#083344", navText: "#CFFAFE", btnText: "#FFFFFF", shadow: "rgba(14,116,144,0.12)" } }),
  carbon: make({ id: "carbon", name: "Carbon", emoji: "⚡", tagline: "Pure black · neon yellow · mono",
    font: '"Lucida Console", Monaco, "JetBrains Mono", monospace',
    colors: { primary: "#EAB308", secondary: "#FACC15", accent: "#FDE047", background: "#09090B", surface: "#18181B", surfaceAlt: "#27272A", text: "#FAFAFA", textMuted: "#A1A1AA", border: "#3F3F46", navBg: "#000000", navText: "#FACC15", btnText: "#09090B", shadow: "rgba(234,179,8,0.2)" } }),
  sunset: make({ id: "sunset", name: "Sunset Peach", emoji: "🌅", tagline: "Warm orange · Optima",
    font: 'Optima, "Segoe UI", sans-serif',
    colors: { primary: "#EA580C", secondary: "#F97316", accent: "#FB923C", background: "#FFF7ED", surface: "#FFFFFF", surfaceAlt: "#FFEDD5", text: "#431407", textMuted: "#C2410C", border: "#FED7AA", navBg: "#431407", navText: "#FED7AA", btnText: "#FFFFFF", shadow: "rgba(234,88,12,0.15)" } }),
  lavender: make({ id: "lavender", name: "Lavender Mist", emoji: "💜", tagline: "Soft violet · Garamond",
    font: 'Garamond, "Times New Roman", serif',
    colors: { primary: "#7C3AED", secondary: "#8B5CF6", accent: "#C4B5FD", background: "#FAF5FF", surface: "#FFFFFF", surfaceAlt: "#EDE9FE", text: "#2E1065", textMuted: "#6D28D9", border: "#DDD6FE", navBg: "#2E1065", navText: "#DDD6FE", btnText: "#FFFFFF", shadow: "rgba(124,58,237,0.15)" } }),
  golden: make({ id: "golden", name: "Golden Hour", emoji: "✨", tagline: "Amber luxe · Baskerville",
    font: 'Baskerville, "Times New Roman", serif',
    colors: { primary: "#B45309", secondary: "#D97706", accent: "#FCD34D", background: "#FFFBEB", surface: "#FFFFFF", surfaceAlt: "#FEF3C7", text: "#451A03", textMuted: "#92400E", border: "#FDE68A", navBg: "#451A03", navText: "#FDE68A", btnText: "#FFFFFF", shadow: "rgba(180,83,9,0.15)" } }),
  neon: make({ id: "neon", name: "Neon City", emoji: "🌆", tagline: "Cyberpunk green · Consolas",
    font: 'Consolas, "Courier New", monospace',
    colors: { primary: "#22C55E", secondary: "#4ADE80", accent: "#86EFAC", background: "#020617", surface: "#0F172A", surfaceAlt: "#1E293B", text: "#F0FDF4", textMuted: "#4ADE80", border: "#166534", navBg: "#000000", navText: "#4ADE80", btnText: "#020617", shadow: "rgba(34,197,94,0.25)" } }),
  analytics_dark: make({ id: "analytics_dark", name: "Analytics Dark", emoji: "📊", tagline: "Deep slate · emerald-to-purple gradient",
    font: 'Inter, system-ui, sans-serif',
    colors: { primary: "#34D399", secondary: "#A78BFA", accent: "#22D3EE", background: "#020617", surface: "#1E293B", surfaceAlt: "#0F172A", text: "#F8FAFC", textMuted: "#94A3B8", border: "#334155", navBg: "#020617", navText: "#F8FAFC", btnText: "#020617", shadow: "rgba(52,211,153,0.18)" } }),
  clinical_blue: make({ id: "clinical_blue", name: "Clinical Blue", emoji: "💎", tagline: "Deep ocean · cyan-teal accents",
    font: 'Inter, system-ui, sans-serif',
    colors: { primary: "#22D3EE", secondary: "#60A5FA", accent: "#5EEAD4", background: "#082F49", surface: "#0C4A6E", surfaceAlt: "#075985", text: "#F0F9FF", textMuted: "#93C5FD", border: "#1E40AF", navBg: "#082F49", navText: "#F0F9FF", btnText: "#082F49", shadow: "rgba(34,211,238,0.22)" } }),
  tech_dark: make({ id: "tech_dark", name: "Tech Dark", emoji: "⚡", tagline: "Purple-pink-cyan tri-gradient",
    font: 'Inter, system-ui, sans-serif',
    colors: { primary: "#A78BFA", secondary: "#F472B6", accent: "#22D3EE", background: "#0A0A0F", surface: "#1F1B3A", surfaceAlt: "#2A2244", text: "#F5F3FF", textMuted: "#C4B5FD", border: "#4C1D95", navBg: "#0A0A0F", navText: "#F5F3FF", btnText: "#0A0A0F", shadow: "rgba(167,139,250,0.22)" } }),
};

const ThemeCtx = createContext(null);
const STORAGE_KEY = "cc_theme";
const FOOTER_KEY = "cc_palette_footer";

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try { const v = localStorage.getItem(STORAGE_KEY); return v && THEMES[v] ? v : "sage"; } catch { return "sage"; }
  });
  const [showFooter, setShowFooterState] = useState(() => {
    try { return localStorage.getItem(FOOTER_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    const t = THEMES[theme]; if (!t) return;
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.style.setProperty("--font-sans", t.fontSans);
    root.style.setProperty("--font-display", t.fontDisplay);
    Object.entries(t.tokens).forEach(([k, v]) => root.style.setProperty(`--${k}`, v));
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-palette-footer", showFooter ? "1" : "0");
    try { localStorage.setItem(FOOTER_KEY, showFooter ? "1" : "0"); } catch {}
  }, [showFooter]);

  const setTheme = (id) => { if (THEMES[id]) setThemeState(id); };
  const setShowFooter = (v) => setShowFooterState(!!v);

  return (
    <ThemeCtx.Provider value={{ theme, setTheme, themes: THEMES, showFooter, setShowFooter, current: THEMES[theme] }}>
      {children}
    </ThemeCtx.Provider>
  );
};

export const useTheme = () => useContext(ThemeCtx);
